import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { ProjectTimeEntry } from '../../types';
import { buildProjectTimeDailyChartData, type ProjectTimeChartExtraMinutes } from './projectTimeChartData';
import './ProjectTimeActivityChart.css';

function formatTooltipMinutes(v: number): string {
  if (v < 60) return `${Math.round(v)}m`;
  const h = Math.floor(v / 60);
  const m = Math.round(v % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const ProjectTimeActivityChart: React.FC<{
  entries: ProjectTimeEntry[];
  extraMinutes?: ProjectTimeChartExtraMinutes[];
}> = ({ entries, extraMinutes = [] }) => {
  const { chartData, series, empty } = useMemo(
    () => buildProjectTimeDailyChartData(entries, extraMinutes),
    [entries, extraMinutes],
  );

  if (empty) {
    return (
      <div className="project-time-activity-chart project-time-activity-chart--empty">
        <p>No dated entries yet — log time or work on this project to see activity.</p>
      </div>
    );
  }

  return (
    <div className="project-time-activity-chart">
      <p className="project-time-activity-chart-hint">Minutes per day by team member (stacked). Hover for totals.</p>
      <div className="project-time-activity-chart-inner">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 8, right: 10, left: 6, bottom: 22 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fill: '#9ca3af', fontSize: 9 }}
              tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
              interval="preserveStartEnd"
              minTickGap={28}
              angle={-32}
              textAnchor="end"
              height={48}
            />
            <YAxis
              tick={{ fill: '#9ca3af', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
              tickFormatter={v => (v >= 60 ? `${Math.round(v / 60)}h` : `${v}m`)}
              width={52}
              dx={-2}
            />
            <Tooltip
              contentStyle={{
                background: '#111827',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8,
                fontSize: 11,
              }}
              labelStyle={{ color: '#e5e7eb', marginBottom: 4 }}
              formatter={(value, name) => [
                formatTooltipMinutes(typeof value === 'number' ? value : Number(value) || 0),
                String(name),
              ]}
              labelFormatter={label => String(label)}
            />
            <Legend
              wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
              formatter={value => <span style={{ color: '#d1d5db' }}>{value}</span>}
            />
            {series.map(s => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.displayName}
                stackId="1"
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.65}
                isAnimationActive={true}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ProjectTimeActivityChart;
